import React from "react";
import { useApolloClient } from "@apollo/client";
import * as yup from "yup";
import {
  checkIfFileIsChosen,
  checkIfFilesAreCorrectType,
  checkIfFilesAreTooBig,
} from "components/Form/validation/validationSchema";
import localforage from "localforage";
import GET_CATEGORIES from "queries/getCategories.gql";
import { AiOutlineUser } from "@react-icons/all-files/ai/AiOutlineUser";
import { AiOutlineUsergroupAdd } from "@react-icons/all-files/ai/AiOutlineUsergroupAdd";
import { FaRegHandshake } from "@react-icons/all-files/fa/FaRegHandshake";
import { FULL_TOOLBAR } from "components/TextEditor/Wysiwyg";
import GET_COUNTRIES from "../queries/getCountries.gql";

export const getSplitSteps = (user = null) => [
  {
    step: {
      name: "Beneficiary",
      path: "/beneficiary",
      title: "Who are you raising funds for?",
    },
    form: [
      {
        classes: "w-auto",
        fields: [
          {
            type: "cardSelect",
            subtext: "",
            name: "paymentRecipientType",
            label: "",
            require: true,
            validationType: "string",
            validations: [
              {
                type: "required",
                params: ["Please choose your beneficiary type."],
              },
            ],
            options: () => [
              {
                value: "Individual",
                label: "Myself",
                icon: AiOutlineUser,
              },
              {
                value: "Advocate",
                label: "Someone else",
                icon: FaRegHandshake,
              },
              {
                value: "Organisation",
                label: "Organization, group or collective",
                icon: AiOutlineUsergroupAdd,
              },
            ],
          },
        ],
      },
      {
        fields: [
          {
            type: "submit",
            value: "Get Started",
            loadingMessage: "Please wait...",
            classes: "",
          },
        ],
      },
    ],
  },
  {
    step: {
      name: "Category",
      path: "/category",
      title: "What cause area are you raising funds for?",
    },
    form: [
      {
        classes: "w-auto",
        fields: [
          {
            type: "select",
            defaultValue: "",
            defaultOption: {
              value: "",
              label: "Please select a category",
            },
            subtext: "",
            name: "category",
            label: "",
            require: true,
            validationType: "string",
            validations: [
              {
                type: "required",
                params: ["Please select a category."],
              },
            ],
            options: () =>
              new Promise((resolve) => {
                const client = useApolloClient();
                client.query({
                  query: GET_CATEGORIES,
                }).then((result) => {
                  const categories = result
                    .data
                    .categories
                    .map((category) => ({ ...category, label: `models.category.${category.value}.title` }));
                  resolve({
                    ...result,
                    data: {
                      categories,
                    },
                  });
                });
              }),
          },
        ],
      },
      {
        fields: [
          {
            type: "submit",
            value: "Continue",
            loadingMessage: "Please wait...",
            classes: "",
          },
        ],
      },
    ],
  },
  {
    step: {
      name: "Country",
      path: "/country",
      title: "What country are you based in?",
    },
    form: [
      {
        classes: "w-auto",
        fields: [
          {
            type: "select",
            name: "country",
            label: "",
            defaultValue: "AU",
            subtext:
              "Donations can be accepted from anywhere in the world, but you'll need a bank and ID in one of these eligible countries.",
            validationType: "string",
            validations: [
              {
                type: "required",
                params: ["Please select a country."],
              },
            ],
            options: () =>
              new Promise((resolve) => {
                const client = useApolloClient();
                client.query({
                  query: GET_COUNTRIES,
                }).then((result) => {
                  const countries = result
                    .data
                    .countries
                    .map((country) => ({ ...country, label: `models.country.${country.value}.country_name` }));
                  resolve({
                    ...result,
                    data: {
                      countries,
                    },
                  });
                });
              }),
          },
        ],
      },
      {
        fields: [
          {
            type: "submit",
            value: "Continue",
            loadingMessage: "Please wait...",
            classes: "",
          },
        ],
      },
    ],
  },
  {
    step: {
      name: "Target",
      path: "/target",
      title: "How much do you want to raise?",
    },
    form: [
      {
        fields: [
          {
            type: "currency",
            name: "amount",
            label: "",
            subtext:
                "Your campaign target will be set in the local currency of the country you choose. You can always change your target later.",
            required: true,
            validationType: "number",
            validations: [
              {
                type: "required",
                params: ["Please enter a target amount."],
              },
              {
                type: "typeError",
                params: ["Please enter a target amount only, don't include a currency symbol."],
              },
              {
                type: "positive",
                params: ["Please enter a number bigger than 0"],
              },
              {
                type: "integer",
                params: ["Please enter a whole number"],
              },
            ],
            defaultValue: "",
          },
        ],
      },
      {
        fields: [
          {
            type: "submit",
            value: "Continue",
            loadingMessage: "Please wait...",
            classes: "",
          },
        ],
      },
    ],
  },
  {
    step: {
      name: "Title",
      path: "/title",
      title: "What will you call your campaign?",
    },
    form: [
      {
        fields: [
          {
            type: "text",
            name: "campaign_title",
            label: "",
            subtext: "Don't worry, you'll be able to change this later",
            placeholder: "My first Campaign",
            classes: "",
            required: false,
            defaultValue: "",
            watch: true,
            validationType: "string",
            validations: [
              {
                type: "required",
                params: ["Please include a campaign title."],
              },
              {
                type: "min",
                params: [3, "Please make the campaign title at least 3 characters long."],
              },
              {
                type: "max",
                params: [200, "Your campaign title is too long."],
              },
            ],
          },
        ],
      },
      {
        fields: [
          {
            type: "submit",
            value: "Save and Continue",
            loadingMessage: "Please wait...",
            classes: "",
          },
        ],
      },
    ],
  },
  {
    step: {
      name: "Image",
      path: "/image",
      title: "Choose a banner image for your campaign",
    },
    form: [
      {
        fields: [
          {
            type: "imageCropper",
            name: "coverImage",
            id: "coverImage",
            placeholder: "Choose",
            label: "",
            subtext:
                "Don't worry, you can add videos and more images later. Images must be .jpg, .png, .gif, .webp or .bmp format and under 5mb in file size.",
            required: false,
            validationType: "mixed",
            validations: [
              {
                type: "when",
                params: [
                  "hasLocalImage",
                  {
                    is: (hasLocalImage) => hasLocalImage === false,
                    then: yup
                      .mixed()
                      .test("is-file-chosen", "Please add a cover image.", checkIfFileIsChosen),
                  },
                ],
              },
              {
                type: "test",
                params: ["is-file-oversize", "Please try a smaller file.", checkIfFilesAreTooBig],
              },
              {
                type: "test",
                params: [
                  "is-file-type-correct",
                  "Please only upload .jpg, .png, .gif, .webp or .bmp files.",
                  checkIfFilesAreCorrectType,
                ],
              },
            ],
            onUpdate: (imgSrc) =>
              new Promise((resolve) => {
                localforage.getItem("onBoarding").then((value) => {
                  const onBoarding = value ?? [];
                  const coverImageItem = ["coverImage", imgSrc];

                  const existingItemIndex = onBoarding?.findIndex((k) => k[0] === "coverImage");
                  if (existingItemIndex !== -1) {
                    onBoarding[existingItemIndex][1] = imgSrc;
                  } else {
                    onBoarding.push(coverImageItem);
                  }

                  localforage.setItem("onBoarding", onBoarding);
                });

                resolve(imgSrc);
              }),
          },
        ],
      },
      {
        fields: [
          {
            type: "submit",
            value: "Save and Continue",
            loadingMessage: "Please wait...",
            classes: "",
          },
        ],
      },
    ],
  },
  {
    step: {
      name: "You",
      path: "/you",
      title: "Tell us about yourself",
    },
    form: getUserForm(user),
  },
  {
    step: {
      name: "Story",
      path: "/story",
      title: "What are you raising funds for?",
      subtitle: "",
    },
    form: [
      {
        classes: "w-auto",
        fields: [
          {
            type: "wysiwyg",
            name: "content",
            label: "",
            toolbar: FULL_TOOLBAR,
            subtext:
                "Tell donors about your campaign. You should include background about the issue; who you are, what you're doing and why you're doing it; what you're spending money on. The best campaign descriptions are around 2,500 characters (or 500 words) long. Don’t worry, you can always change this later.",
            required: true,
            validationType: "string",
            validations: [
              {
                type: "required",
                params: ["Please add campaign description."],
              },
              {
                type: "minText",
                params: [100, "Your description should be at least 100 characters long."],
              },
            ],
          },
        ],
      },
      {
        classes: "terms-consent",
        fields: [
          {
            type: "checkbox",
            name: "termsAgreedAt",
            classes: ["campaign-create-form__checkbox"],
            label: ({ t }) => (
              <span>
                {t("I agree with Chuffed.org's")}
                {" "}
                <a href="/terms-conditions" target="_blank" rel="noreferrer">
                  {t("Terms and Conditions")}
                </a>
                ,
                {" "}
                <a href="/privacy" target="_blank" rel="noreferrer">
                  {t("Privacy Policy")}
                </a>
                {" "}
                {t("and")}
                {" "}
                <a href="/acceptable-use-policy" target="_blank" rel="noreferrer">
                  {t("Acceptable Use Policy")}
                </a>
                .
              </span>
            ),
            required: true,
            defaultValue: false,
            validationType: "boolean",
            validations: [
              {
                type: "required",
                params: ["The terms and conditions must be accepted"],
              },
              {
                type: "oneOf",
                params: [[true], "The terms and conditions must be accepted"],
              },
            ],
          },
        ],
      },
      {
        fields: [
          {
            type: "submit",
            value: "Create Campaign",
            loadingMessage: "Creating Campaign...",
            classes: "",
          },
        ],
      },
    ],
  },
  {
    step: {
      name: "Payments",
      path: "/payments",
      title: "Congrats! We’ve created your campaign",
      subtitle: "Don’t worry if it’s not perfect yet, you can still work on it before you launch",
    },
    form: [
      {
        fields: [
          {
            type: "submit",
            value: "Set Up Payments",
            loadingMessage: "Redirecting to Stripe...",
            classes: "",
          },
        ],
      },
    ],
    bodyCopy: {
      title: ({ t }) => (
        <>
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg"
            className="logo"
            alt={t("Stripe logo")}
          />
          <h4>
            {t(
              "Setting up payments with Stripe is essential for both organisations and individuals, so that you can receive donations.",
            )}
          </h4>
        </>
      ),
      body: ({ t, paymentRecipientType }) => (
        <>
          <p>
            {t(`For individuals, when Stripe asks your business type, you can choose “individual” or
            “individual/sole trader”.`)}
          </p>
          <p className="mb-0">
            {t(
              "You'll need some personal information on hand to set up your Stripe account, such as:",
            )}
          </p>
          <ul>
            <li>{t("The bank account details for where you'd like to receive donations")}</li>
            <li>{t("You may need to verify you phone number and/or email address")}</li>
            <li>{t("Identification, and other identifying information, may be needed")}</li>
          </ul>
          {paymentRecipientType === "Advocate" && (
            <div style={{ fontWeight: 600, marginBottom: "1rem" }}>
              <p>
                {t("To fundraise on behalf of another person, you must have:")}
              </p>
              <ul>
                <li>{t("verified the identify of the person you are going to send the funds to, and")}</li>
                <li>{t("keep a record of all funds transferred to the individual")}</li>
              </ul>
            </div>
          )}
          <p style={{ textAlign: "center" }}>
            <a target="_blank" href="https://docs.chuffed.org/article/77-stripe" rel="noreferrer">
              {t("Click here for more information on setting up your payments.")}
            </a>
          </p>
        </>
      ),
    },
  },
];

const getUserForm = (user) => {
  const fields = [
    {
      fields: [
        {
          type: "text",
          name: "firstName",
          label: "First Name",
          classes: "",
          required: false,
          defaultValue: "",
          validationType: "string",
          validations: [
            {
              type: "required",
              params: ["Please enter your name."],
            },
            {
              type: "min",
              params: [2, "Your name can't be shorter than 2 characters long."],
            },
          ],
        },
        {
          type: "text",
          name: "lastName",
          label: "Last Name",
          classes: "",
          required: false,
          defaultValue: "",
          validationType: "string",
          validations: [
            {
              type: "required",
              params: ["Please enter your surname."],
            },
            {
              type: "min",
              params: [2, "Your surname can't be shorter than 2 characters long."],
            },
          ],
        },
      ],
    },
    {
      fields: [
        {
          type: "text",
          name: "email",
          label: "Email",
          classes: "",
          required: false,
          defaultValue: "",
          validationType: "string",
          validations: [
            {
              type: "required",
              params: ["Please include your email address."],
            },
            {
              type: "email",
              params: ["Please check your email address."],
            },
          ],
        },
      ],
    },
  ];

  if (!user) {
    fields.push({
      fields: [
        {
          type: "password",
          name: "password",
          label: "Password",
          classes: "",
          required: false,
          defaultValue: "",
          dataTestId: "onboarding-password",
          validationType: "string",
          validations: [
            {
              type: "required",
              params: ["Please enter a password"],
            },
            {
              type: "min",
              params: [8, "Your password must be at least 8 characters long"],
            },
            {
              type: "matches",
              params: [/^.*(?=.{8,})(?=.*\d)((?=.*[a-zA-Z]){1}).*$/, "Your password must contain a mix letters and numbers"],
            },
          ],
        },
        {
          type: "password",
          name: "password_confirmation",
          label: "Confirm Password",
          classes: "",
          required: false,
          defaultValue: "",
          validationType: "string",
          validations: [
            {
              type: "required",
              params: ["Please confirm your password"],
            },
            {
              type: "oneOf",
              params: [[yup.ref("password"), null], "Passwords must match"],
            },
          ],
        },
      ],
    });
  }

  fields.push({
    fields: [
      {
        type: "submit",
        value: "Save and Continue",
        loadingMessage: "Please wait...",
        classes: "",
      },
    ],
  });

  return fields;
};
